import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Facebook, Instagram, Mail, Phone, MapPin, Heart } from 'lucide-react';

const Footer = () => {
  const { t, isRTL } = useLanguage();

  const quickLinks = [
    { path: '/', label: t('home') },
    { path: '/churches', label: t('churches') },
    { path: '/news', label: t('news') },
    { path: '/events', label: t('events') },
  ];

  const churches = [
    {
      name: 'Vår Fru av Fatima kyrka',
      location: 'Karlskrona',
      //phone: '076-201 77 67',
      email: 'karlskrona@katolskakyrkan.se',
      mapsUrl: 'https://www.google.com/maps/dir/?api=1&destination=Södra+Kungsgatan+1,+371+31+Karlskrona,+Sweden',
    },
    {
      name: 'S:t Bonifatius kyrka',
      location: 'Karlshamn',
      //phone: '076-201 77 67',
      email: 'karlshamn@fatimakrona.se',
      mapsUrl: 'https://www.google.com/maps/dir/?api=1&destination=Norra+Fogdelyckegatan+26,+374+38+Karlshamn,+Sweden',
    },
    {
      name: 'S:t Antonius kyrka',
      location: 'Olofström',
      //phone: '073-396 84 82',
      email: 'olofstrom@fatimakrona.se',
      mapsUrl: 'https://www.google.com/maps/dir/?api=1&destination=Bruksgatan+8,+293+38+Olofström,+Sweden',
    },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <h3 className="text-lg font-serif font-bold text-yellow-400 mb-4">{t('parishName')}</h3>
            <p className="text-sm text-gray-400 mb-4">
              {t('heroSubtitle')}
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/profile.php?id=100079612419781"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center hover:bg-blue-700 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5 text-white" />
              </a>
              {/*<a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center hover:from-purple-700 hover:to-pink-700 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-white" />
              </a>*/}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-serif font-bold text-yellow-400 mb-4">Snabblänkar</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-yellow-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/bible"
                  className="text-sm text-gray-400 hover:text-yellow-400 transition-colors"
                >
                  {t('bible')}
                </Link>
              </li>
              {/* <li>
                <Link
                  to="/gallery"
                  className="text-sm text-gray-400 hover:text-yellow-400 transition-colors"
                >
                  {t('gallery')}
                </Link>
              </li> */}
            </ul>
          </div>

          {/* Churches */}
          <div className="lg:col-span-2">
            <h3 className="text-lg font-serif font-bold text-yellow-400 mb-4">{t('ourChurches')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {churches.map((church) => (
                <div key={church.name} className="text-sm">
                  <h4 className="font-semibold text-white mb-2">{church.name}</h4>
                  <div className="space-y-1 text-gray-400">
                    <a
                      href={church.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 hover:text-yellow-400 transition-colors cursor-pointer"
                      title={`${t('getDirections') || 'Få vägbeskrivning'} - ${church.location}`}
                    >
                      <MapPin className="w-3 h-3 text-yellow-400" />
                      <span className="hover:underline">{church.location}</span>
                    </a>
                    {/*<div className="flex items-center space-x-2">
                      <Phone className="w-3 h-3" />
                      <span>{church.phone}</span>
                    </div>*/}
                    <div className="flex items-center space-x-2">
                      <Mail className="w-3 h-3" />
                      <span className="text-xs">{church.email}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Donation */}
        <div className="mt-8 pt-8 border-t border-gray-700">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Swish */}
              <div className="flex items-center space-x-3">
                <Heart className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-sm font-semibold text-white">{t('donate')} via Swish</p>
                  <p className="text-lg font-bold text-yellow-400">123-624 89 75</p>
                </div>
              </div>
              
              {/* Bankgiro */}
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-white">Bankgiro</p>
                  <p className="text-lg font-bold text-green-400">815-6085</p>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} {t('parishName')}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;